import {
    Box,
    Button, Checkbox, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormSection from '../../../components/form-section';
import FormStack from '../../../components/form-stack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { translate } from '../../../i18n';
import {
    availabilitySubmitBlockerToast,
    findAvailabilitySubmitBlocker,
} from '../utils/availability-submit-blocker';
import { spacing } from '../../../theme/tokens';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { CreateCourtFormAvailabilityInputType, SLOTS_PER_HOUR, transformInputToRule } from '../../../models/court/dto/court-availability-template.constants';
import { buildCompactAvailabilityBlocks } from '../utils/build-compact-availability-blocks';
import { SportType } from '../../../models/court/dto/court.dto';
import { EventType } from '../../../models/event/dto/event.dto';
import { OrganizationLocationDto } from '../../../models/location/dto/location.dto';
import { getDefaultCurrency } from '../../court-pricing-management/components/CurrencyPicker';
import { useCreateEvent } from '../../event-management/hooks/useCreateEvent';
import LocationPicker from '../../location-management/components/LocationPicker';
import { useSingleClubAutoSelect } from '../../location-management/hooks/useSingleClubAutoSelect';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useCreateCourt } from '../hooks/useCreateCourt';
import AddAvailabilitiesForm from './AddAvailabilitiesForm';
import CourtAvailabilityTypePicker from './CourtAvailabilityTypePicker';
import SportSelect from 'components/SportSelect';
import SubCourtPicker from './SubCourtPicker';

export const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export function numberToHourMinuteLabel(x: number, slotsPerHour: number = SLOTS_PER_HOUR) {
    const minutesPerSlot = 60 / slotsPerHour;
    const minutes = (x % slotsPerHour) * minutesPerSlot;
    const hours = Math.floor(x / slotsPerHour);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}
export function createDateObjects({ startMinute, startHour, endHour, endMinute, dayOfWeek }: { dayOfWeek: number, startHour: number, startMinute: number, endHour: number, endMinute: number }) {
    // Get today's date
    const today = new Date();

    // Calculate the date for the specified day of the week
    const targetDate = new Date(today);
    const currentDay = today.getDay(); // 0 (Sunday) to 6 (Saturday)

    // Calculate the difference in days
    const dayDifference = (dayOfWeek - currentDay + 7) % 7; // Ensures positive difference
    targetDate.setDate(today.getDate() + dayDifference); // Set to target day

    // Create start and end date objects
    const startDate = new Date(targetDate);
    startDate.setHours(startHour, startMinute, 0, 0); // Set start hour

    const endDate = new Date(targetDate);
    endDate.setHours(endHour, endMinute, 0, 0); // Set end hour

    return { startDate, endDate };
}
const getNewAvailability = (): CreateCourtFormAvailabilityInputType => ({
    slotDurationInMs: 3600000,
    id: v4(),
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    min: 8 * SLOTS_PER_HOUR,
    max: 12 * SLOTS_PER_HOUR,
    configurations: [{
        id: v4(),
        slotDurationInMs: 3600000,
        currency: getDefaultCurrency(),
        sport: SportType.PADEL
    }]
});
export type CreateCourtFormInputType = {
    name: string;
    sportTypes: SportType[];
    availabilities: CreateCourtFormAvailabilityInputType[];
    currency: Currency;
    price: number;
    defaultHourlyPriceDuration: number;
    location?: OrganizationLocationDto;
    isComposite?: boolean;
    subCourtIds?: number[];
    isOutdoor?: boolean;
    coachingPriority?: number;
    createEvent?: boolean;
    eventData?: {
        name: string;
        startTime: string;
        endTime: string;
        capacity: number;
        location: string;
        notificationTitle: string;
    };
};

export default function CreateCourtForm() {
    const navigation = useNavigate();
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [endDate, setEndDate] = useState(() => {
        const date = new Date(new Date().getTime() + 364 * 24 * 60 * 60 * 1000);
        date.setHours(0, 0, 0, 0)
        return date;
    });
    const [isGameAvailable, setIsGameAvailable] = useState(true);
    const [isCoachingAvailable, setIsCoachingAvailable] = useState(false);


    const { selectedLocation } = useSelectedLocation();
    const { mutateAsync: createCourt, isLoading: isCreating } = useCreateCourt((newlyCreatedCourt) => {
        navigation(`/admin/courtDetails?court=${newlyCreatedCourt.id}`);
    });
    const { mutateAsync: createEvent } = useCreateEvent();
    const [returnAsOneChunk, setReturnAsOneChunk] = useState(false);
    const toast = useToast();

    useEffect(() => {
        setFormData(f => ({ ...f, location: selectedLocation }));
    }, [selectedLocation]);

    const [formData, setFormData] = useState<CreateCourtFormInputType>({
        name: '',
        availabilities: [getNewAvailability()],
        sportTypes: [],
        location: selectedLocation,
        currency: Currency.EUR,
        price: 10,
        defaultHourlyPriceDuration: 60 * 60 * 1000, // 1 hour in milliseconds
        isComposite: false,
        subCourtIds: [],
        isOutdoor: false,
        createEvent: false,
        eventData: {
            name: '',
            startTime: '',
            endTime: '',
            capacity: 0,
            location: '',
            notificationTitle: ''
        }
    });
    const { isOpen, onOpen, onClose } = useDisclosure();


    const closeModal = () => {
        onClose();
        setFormData({
            name: '',
            availabilities: [getNewAvailability()],
            sportTypes: [],
            currency: Currency.EUR,
            price: 10,
            location: formData.location,
            defaultHourlyPriceDuration: 60 * 60 * 1000,
            isComposite: false,
            subCourtIds: [],
            isOutdoor: false,
            createEvent: false,
            eventData: {
                name: '',
                startTime: '',
                endTime: '',
                capacity: 0,
                location: '',
                notificationTitle: ''
            }
        });
    };

    const handleInputChange = (key: keyof typeof formData, value: any) => {
        setFormData({ ...formData, [key]: value });
    };

    // MYS-66: hide the club selector when the org has a single club; the sole
    // club is auto-selected into formData.location in the background.
    const { isSingleClub } = useSingleClubAutoSelect({
        selectedLocation: formData.location,
        onSelect: (loc) => handleInputChange('location', loc),
    });

    const handleEventDataChange = (field: keyof NonNullable<typeof formData.eventData>, value: any) => {
        setFormData({
            ...formData,
            eventData: {
                name: formData.eventData?.name || '',
                startTime: formData.eventData?.startTime || '',
                endTime: formData.eventData?.endTime || '',
                capacity: formData.eventData?.capacity || 0,
                location: formData.eventData?.location || '',
                notificationTitle: formData.eventData?.notificationTitle || '',
                [field]: value
            }
        });
    };

    const onAddAvailability = () => {
        setFormData(f => {
            return {
                ...f,
                availabilities: [...f.availabilities, getNewAvailability()]
            }
        });
    };
    const onRemoveAvailability = () => {
        setFormData(f => {
            return {
                ...f,
                availabilities: // remove last one
                    f.availabilities.slice(0, f.availabilities.length - 1)
            }
        });
    };

    const handleSubmit = async () => {
        // Validate event data if creating event
        if (formData.createEvent) {
            if (!formData.eventData?.name || !formData.eventData?.location ||
                !formData.eventData?.startTime || !formData.eventData?.endTime ||
                !formData.eventData?.capacity || formData.eventData.capacity <= 0) {
                return; // Form validation will show the errors
            }
        }

        // Guards, in the shared order. A config price parses to `undefined`
        // when left empty — the RESOLVED price is what matters here, since a
        // config price falls back to the court default before it is sent. Then
        // two prices for the same sport + duration + capacity inside one
        // availability block make the price ambiguous and the API rejects the
        // whole batch with a 400; the message names the capacity rather than
        // failing generically. Grouped per block — overlapping availabilities
        // may legitimately repeat a triple.
        const blocker = findAvailabilitySubmitBlocker(
            formData.availabilities.map(a => a.configurations),
            formData.availabilities.flatMap(a =>
                a.configurations.map(c => c.price ?? formData.price)
            ),
        );
        if (blocker) {
            const { title, durationMs } = availabilitySubmitBlockerToast(blocker);
            toast({
                title,
                status: 'error',
                duration: durationMs,
                isClosable: true,
            });
            return;
        }

        try {
            // Send the compact schedule and let the server expand it (same
            // approach as AddAvailabilitiesModal / expand-compact-availabilities)
            // instead of building tens of thousands of objects in the browser,
            // which froze/crashed the tab for long date ranges.
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const blocks = buildCompactAvailabilityBlocks(formData.availabilities, {
                price: formData.price,
                sport: formData.sportTypes[0],
                currency: formData.currency,
            });
            const newCourt = await createCourt({
                locationId: selectedLocation?.id,
                sportTypes: formData.sportTypes,
                defaultHourlyPrice: formData.price,
                defaultHourlyPriceCurrency: formData.currency,
                defaultHourlyPriceDuration: formData.defaultHourlyPriceDuration,
                name: formData.name,
                isComposite: formData.isComposite,
                subCourtIds: formData.subCourtIds,
                isOutdoor: formData.isOutdoor,
                coachingPriority: isCoachingAvailable ? formData.coachingPriority : undefined,
                availabilityTemplate: {
                    id: v4(),
                    courtId: Number(selectedLocation?.id),
                    startDate: startDate,
                    endDate: endDate,
                    appliedToAllTime: true,
                    isGameAvailable: isGameAvailable,
                    isCoachingAvailable: isCoachingAvailable,
                    returnAsOneChunk: returnAsOneChunk,
                    rules: formData.availabilities.map(a => transformInputToRule(a))
                },
                compactAvailabilities: {
                    timeZone,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    returnAsOneChunk,
                    blocks,
                }
            });

            // Create event if requested
            if (formData.createEvent && formData.eventData && selectedLocation) {
                try {
                    await createEvent({
                        name: formData.eventData.name,
                        startTime: formData.eventData.startTime,
                        endTime: formData.eventData.endTime,
                        capacity: formData.eventData.capacity,
                        location: formData.eventData.location,
                        notificationTitle: formData.eventData.notificationTitle,
                        locationId: selectedLocation.id,
                        eventType: EventType.CLASS,
                        sportType: formData.sportTypes[0],
                        courtIds: [newCourt.id]
                    });
                    toast({
                        title: translate('courts.court_and_event_created'),
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                } catch (eventError: any) {
                    const errorMessage = eventError?.response?.data?.message || eventError?.message || translate('error');
                    toast({
                        title: translate('courts.court_created_event_failed'),
                        description: errorMessage,
                        status: 'warning',
                        duration: 10000,
                        isClosable: true,
                        position: 'top',
                    });
                }
            } else {
                toast({
                    title: translate('courts.court_created'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }

            onClose();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || translate('error');
            toast({
                title: translate('courts.create_court_error'),
                description: errorMessage,
                status: 'error',
                duration: 10000,
                isClosable: true,
                position: 'top',
            });
        }
    };
    const sectionBg = useColorModeValue('gray.50', 'whiteAlpha.50');

    return (
        <>
            <Button colorScheme="brand" onClick={onOpen}>
                {translate('courts.add_new_court')}
            </Button>
            <FormModal
                isOpen={isOpen}
                onClose={closeModal}
                title={translate('courts.add_new_court')}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                cancelLabel={translate('cancel')}
                isSubmitting={isCreating}
                size="4xl"
            >
                <FormStack density="section">
                    {/* ── Section 1: Court Identity ── */}
                    <FormSection label={translate('courts.title_in_form')}>
                        <FormField
                            type="text"
                            label={translate('courts.title_in_form')}
                            value={formData.name}
                            onChange={(value) => handleInputChange('name', value)}
                            isRequired
                        />

                        <FormControl>
                            <FormLabel>{translate('courts.sports')}</FormLabel>
                            <SportSelect
                                value={formData.sportTypes}
                                onChange={(newValue) => handleInputChange('sportTypes', newValue)}
                            />
                        </FormControl>

                        {!isSingleClub && (
                            <FormControl isInvalid={!formData.location}>
                                <FormLabel>{translate('courts.club')}</FormLabel>
                                <LocationPicker
                                    selectedLocation={formData.location}
                                    setSelectedLocation={(e) => handleInputChange('location', e)}
                                />
                                <FormErrorMessage>{translate('courts.must_select_club')}</FormErrorMessage>
                            </FormControl>
                        )}
                    </FormSection>

                    {/* ── Section 2: Court Structure ── */}
                    <FormSection label={translate('courts.is_composite')}>
                        <FormControl>
                            <Checkbox
                                isChecked={formData.isComposite}
                                onChange={(e) => {
                                    const isComposite = e.target.checked;
                                    setFormData(prevData => ({
                                        ...prevData,
                                        isComposite,
                                        subCourtIds: isComposite ? prevData.subCourtIds : [],
                                    }));
                                }}
                                colorScheme="brand"
                                size="lg"
                            >
                                {translate('courts.is_composite')}
                            </Checkbox>
                            <FormHelperText>{translate('courts.is_composite_helper')}</FormHelperText>
                        </FormControl>

                        {formData.isComposite && (
                            <SubCourtPicker
                                selectedSubCourtIds={formData.subCourtIds || []}
                                setSelectedSubCourtIds={(subCourtIds) =>
                                    handleInputChange('subCourtIds', subCourtIds)
                                }
                            />
                        )}

                        <FormControl>
                            <Checkbox
                                isChecked={formData.isOutdoor}
                                onChange={(e) => handleInputChange('isOutdoor', e.target.checked)}
                                colorScheme="brand"
                                size="lg"
                            >
                                {translate('courts.is_outdoor')}
                            </Checkbox>
                            <FormHelperText>{translate('courts.is_outdoor_description')}</FormHelperText>
                        </FormControl>
                    </FormSection>

                    {/* ── Section 3: Availability Type ── */}
                    <FormSection label={translate('courts.court_availability_type_picker.title')}>
                        <CourtAvailabilityTypePicker
                            isGameAvailable={isGameAvailable}
                            setIsGameAvailable={setIsGameAvailable}
                            isCoachingAvailable={isCoachingAvailable}
                            setIsCoachingAvailable={setIsCoachingAvailable}
                        />
                        {isCoachingAvailable && (
                            <FormField
                                type="number"
                                label={translate('courts.coaching_priority')}
                                helperText={translate('courts.coaching_priority_helper')}
                                value={formData.coachingPriority ?? ''}
                                min={1}
                                step={1}
                                placeholder={translate('courts.coaching_priority_placeholder')}
                                onChange={(value) => handleInputChange('coachingPriority', value)}
                                onEmpty={() => handleInputChange('coachingPriority', undefined)}
                            />
                        )}
                    </FormSection>

                    {/* ── Section 4: Event (optional) ── */}
                    <FormSection label={translate('courts.create_event_with_court')}>
                        <FormControl>
                            <Checkbox
                                isChecked={formData.createEvent}
                                onChange={(e) => handleInputChange('createEvent', e.target.checked)}
                                colorScheme="brand"
                                size="lg"
                            >
                                {translate('courts.create_event_with_court')}
                            </Checkbox>
                            <FormHelperText>{translate('courts.create_event_with_court_helper')}</FormHelperText>
                        </FormControl>

                        {formData.createEvent && (
                            <Box bg={sectionBg} borderRadius="12px" p={4}>
                                <Flex direction="column" gap={spacing.fieldGap}>
                                    <FormField
                                        type="text"
                                        label={translate('event.name')}
                                        value={formData.eventData?.name || ''}
                                        onChange={(value) => handleEventDataChange('name', value)}
                                        isRequired
                                        error={!formData.eventData?.name && formData.eventData?.name !== undefined ? translate('form.cannot_be_empty') : undefined}
                                    />

                                    <FormField
                                        type="text"
                                        label={translate('event.location')}
                                        value={formData.eventData?.location || ''}
                                        onChange={(value) => handleEventDataChange('location', value)}
                                        isRequired
                                        helperText={translate('event.location_helper')}
                                    />

                                    <Flex gap={4}>
                                        <Box flex={1}>
                                            <FormField
                                                type="datetime-local"
                                                label={translate('event.start_time')}
                                                value={formData.eventData?.startTime || ''}
                                                onChange={(value) => handleEventDataChange('startTime', value)}
                                                isRequired
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <FormField
                                                type="datetime-local"
                                                label={translate('event.end_time')}
                                                value={formData.eventData?.endTime || ''}
                                                onChange={(value) => handleEventDataChange('endTime', value)}
                                                isRequired
                                            />
                                        </Box>
                                    </Flex>

                                    <Flex gap={4}>
                                        <Box flex={1}>
                                            <FormField
                                                type="number"
                                                label={translate('event.capacity')}
                                                value={formData.eventData?.capacity || 0}
                                                onChange={(value) => handleEventDataChange('capacity', value)}
                                                min={1}
                                                isRequired
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <FormField
                                                type="text"
                                                label={translate('event.notification_title')}
                                                value={formData.eventData?.notificationTitle || ''}
                                                onChange={(value) => handleEventDataChange('notificationTitle', value)}
                                                helperText={translate('event.notification_title_helper')}
                                            />
                                        </Box>
                                    </Flex>
                                </Flex>
                            </Box>
                        )}
                    </FormSection>

                    {/* ── Section 5: Availability Schedule ── */}
                    <FormSection label={translate('courts.availabilities')}>
                        <AddAvailabilitiesForm
                            returnAsOneChunk={returnAsOneChunk}
                            setReturnAsOneChunk={setReturnAsOneChunk}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            availabilities={formData.availabilities}
                            onAddAvailability={onAddAvailability}
                            onRemoveAvailability={onRemoveAvailability}
                            onAvailabilitiesChange={(newAvailabilities) => {
                                setFormData((f) => ({ ...f, availabilities: newAvailabilities }));
                            }}
                        />
                    </FormSection>
                </FormStack>
            </FormModal>
        </>
    )
}
